import DiscontinueSubscriptionLineModal from 'components/pages/SubscriptionLines/DiscontinueSubscriptionLineModal/DiscontinueSubscriptionLineModal';
import styles from './SubscriptionLines.module.css';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { formatDate, localizePathname } from '../../../util/util';
import { useTranslation } from 'react-i18next';
import SubscriptionLinesDataGrid from './SubscriptionLinesDataGrid/SubscriptionLinesDataGrid';
import IconButton from '@standardsdigital/ui-components/IconButton';
import { Tab, TabList, TabPanel, Tabs } from '@mui/joy';
import type {
  GridCellParams,
  GridColDef,
  GridFilterModel,
} from '@mui/x-data-grid-pro';
import type {
  CollectionModel,
  StandardModel,
  EformModel,
} from 'redux/services/api.generated';
import {
  useBulkReactivateSubscriptionLinesMutation,
  useGetSubscriptionQuery,
  useListSubscriptionCollectionsQuery,
  useListSubscriptionStandardsQuery,
  useListSubscriptionEformsQuery,
  LineStatus,
  DocumentServices,
} from 'redux/services/api.generated';
import { useEffect, useEffectEvent, useState } from 'react';
import { setAppTitle } from 'redux/slices/appSlice';
import { useAppDispatch } from 'redux/hooks';
import Spinner from 'components/global/Spinner/Spinner';
import { useToast } from 'util/hooks';
import SubscriptionDetails from './SubscriptionDetails/SubscriptionDetails';
import type { StatusIndicatorProps } from 'components/global/StatusIndicator/StatusIndicator';
import StatusIndicator from 'components/global/StatusIndicator/StatusIndicator';
import { api } from 'redux/services/api';
import CacheTag from 'redux/services/cacheTags';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import ProductSearch from 'components/pages/SubscriptionLines/ProductSearch/ProductSearch';
import LinkButton from '@standardsdigital/ui-components/LinkButton';
import ScreenReaderOnly from '@standardsdigital/ui-components/ScreenReaderOnly';
import { SubscriptionLinesTabEnum } from 'types/subscriptionLines';
import { routes } from 'util/routes';
import {
  defaultStandardColumnSizes,
  standardColumnSizeOverrides,
  defaultCollectionColumnSizes,
  collectionColumnSizeOverrides,
  defaultEformColumnSizes,
  eformColumnSizeOverrides,
  resolveColumnSizes,
} from './SubscriptionLines.columnSizes';

function SubscriptionLines() {
  const { t, i18n } = useTranslation();
  const { subscriptionId } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [discontinueModalOpen, setDiscontinueModalOpen] = useState(false);

  const [includeStandardsInCollections, setIncludeStandardsInCollections] =
    useState(false);

  const [selectedTab, setSelectedTab] = useState<SubscriptionLinesTabEnum | null>(
    null,
  );

  const [activeSubscriptionLine, setActiveSubscriptionLine] = useState<{
    id: number;
    name: string;
    status: StandardModel['lineStatus'];
  } | null>(null);

  const [standardsFilterModel, setStandardsFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [collectionsFilterModel, setCollectionsFilterModel] =
    useState<GridFilterModel>({ items: [] });

  const [eformsFilterModel, setEformsFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const {
    data: subscriptionDetails,
    isLoading: isLoadingSubscriptionDetails,
    isError: isFetchSubscriptionDetailsError,
  } = useGetSubscriptionQuery(
    {
      subscriptionId: subscriptionId as string,
    },
    { skip: !subscriptionId },
  );

  const isEform =
    subscriptionDetails?.serviceLevel === 'eForms' &&
    subscriptionDetails.documentServices.includes(DocumentServices.Eform);

  const {
    data: collections,
    isLoading: isLoadingCollections,
    isFetching: isFetchingCollections,
    isError: isFetchCollectionsError,
  } = useListSubscriptionCollectionsQuery(
    {
      subscriptionId: subscriptionId as string,
    },
    { skip: !subscriptionId },
  );

  const {
    data: standards,
    isLoading: isLoadingStandards,
    isFetching: isFetchingStandards,
    isError: isFetchStandardsError,
  } = useListSubscriptionStandardsQuery(
    {
      subscriptionId: subscriptionId as string,
      includeStandardsInCollections,
    },
    { skip: !subscriptionId },
  );

  const {
    data: eforms,
    isLoading: isLoadingEforms,
    isFetching: isFetchingEforms,
    isError: isFetchEformsError,
  } = useListSubscriptionEformsQuery(
    {
      subscriptionId: subscriptionId as string,
    },
    { skip: !subscriptionId },
  );

  const [reactivateSubscriptionLines] = useBulkReactivateSubscriptionLinesMutation();

  const refetch = () => {
    if (!selectedTab || !subscriptionId) {
      return;
    }

    dispatch(
      api.util.invalidateTags([
        { type: CacheTag.SubscriptionLines, id: `${subscriptionId}:${selectedTab}` },
      ]),
    );
  };

  useEffect(() => {
    if (subscriptionDetails && subscriptionId) {
      dispatch(
        setAppTitle({
          translationKey: 'subscriptions.standards',
          options: {
            id: subscriptionId,
            name: subscriptionDetails.description,
          },
        }),
      );
    }
  }, [dispatch, subscriptionDetails, subscriptionId]);

  const initializeSelectedTab = useEffectEvent(() => {
    if (standards?.length) {
      setSelectedTab(SubscriptionLinesTabEnum.Standards);
    } else if (collections?.length) {
      setSelectedTab(SubscriptionLinesTabEnum.Collections);
    } else if (eforms?.length) {
      setSelectedTab(SubscriptionLinesTabEnum.Eforms);
    }
  });

  useEffect(() => {
    if (isLoadingStandards || selectedTab !== null) {
      return;
    }
    initializeSelectedTab();
  }, [standards, collections, eforms, isLoadingStandards, selectedTab]);

  const getStatusColor = (status: LineStatus) => {
    let statusColor: StatusIndicatorProps['color'] = 'neutral';
    switch (status) {
      case LineStatus.Active:
        statusColor = 'success';
        break;
      case LineStatus.PendingInactive:
      case LineStatus.PendingActive:
      case LineStatus.Discontinued:
        statusColor = 'warning';
        break;
      case LineStatus.Inactive:
      case LineStatus.Expired:
        statusColor = 'danger';
        break;
      case LineStatus.Draft:
      case LineStatus.Quotation:
        break;
    }
    return statusColor;
  };

  const getStatusLabel = (
    value: string | undefined,
    row: StandardModel | CollectionModel | EformModel,
  ) => {
    if (!value || !row) {
      return;
    }
    if (row.lineStatus === 'Discontinued') {
      return t('subscriptions.discontinuedFrom', {
        date: formatDate(row?.inactiveDate) || '',
      });
    }
    if (row?.inactiveDate) {
      return `${value} ${formatDate(row.inactiveDate)}`;
    }
    return value;
  };

  const handleReactivate = (row: StandardModel | CollectionModel | EformModel) => {
    reactivateSubscriptionLines({
      bulkReactivateSubscriptionLinesCommand: {
        subscriptionLineIds: [row.id],
      },
    })
      .unwrap()
      .then(() => {
        refetch();
        toast.success(
          t('subscriptions.subscriptionHasBeenReactivated', {
            subscriptionId: row.id,
          }),
        );
      })
      .catch(() => {
        // Errors are handled in middleware.
      });
  };

  const getRowActionButton = ({
    row,
    tabIndex,
  }: GridCellParams<StandardModel | CollectionModel>) => {
    if (
      row.lineType === 'StandardInCollection' ||
      row.lineStatus === 'Expired' ||
      (subscriptionDetails &&
        ['Inactive', 'Discontinued'].includes(
          subscriptionDetails.subscriptionStatus,
        ))
    ) {
      return;
    }
    if (row.lineStatus === 'Active') {
      return (
        <IconButton
          size="small"
          icon="CalendarOff"
          onClick={() => {
            setActiveSubscriptionLine({
              id: row.id,
              name: row.name,
              status: row.lineStatus,
            });
            setDiscontinueModalOpen(true);
          }}
          tabIndex={tabIndex}
        >
          {t('subscriptions.deactivate')}
        </IconButton>
      );
    }
    if (['Inactive', 'PendingInactive', 'Discontinued'].includes(row.lineStatus)) {
      return (
        <IconButton
          size="small"
          icon="Recycle"
          onClick={() => handleReactivate(row)}
          tabIndex={tabIndex}
        >
          {t('subscriptions.reactivate')}
        </IconButton>
      );
    }
  };

  const defaultColumnProps: Partial<GridColDef> = {
    renderCell: ({ value }) => <span className={styles.ellipsis}>{value}</span>,
  };

  const getSingleSelectOptions = <
    Row extends StandardModel | CollectionModel | EformModel,
    Key extends keyof Row,
  >(
    rows: Row[] | undefined,
    field: Key,
  ): Array<Row[Key]> => {
    if (!rows) {
      return [];
    }
    return [...new Set(rows.map((row) => row[field]))];
  };

  const getStatusValueOptions = <
    Row extends StandardModel | CollectionModel | EformModel,
  >(
    rows: Row[] | undefined,
  ) => {
    const statuses = getSingleSelectOptions(rows, 'lineStatus');
    const localizedOptions = statuses.map((status) =>
      t(`subscriptionStatuses.${status}`),
    );
    return localizedOptions;
  };

  const standardColumnSizes = resolveColumnSizes(
    defaultStandardColumnSizes,
    i18n.language,
    standardColumnSizeOverrides,
  );
  const collectionColumnSizes = resolveColumnSizes(
    defaultCollectionColumnSizes,
    i18n.language,
    collectionColumnSizeOverrides,
  );
  const eformColumnSizes = resolveColumnSizes(
    defaultEformColumnSizes,
    i18n.language,
    eformColumnSizeOverrides,
  );

  const standardColumns: GridColDef[] = [
    {
      ...defaultColumnProps,
      field: 'name',
      headerName: t('labels.standardReference'),
      ...standardColumnSizes.name,
    },
    {
      ...defaultColumnProps,
      field: 'sdo',
      headerName: t('labels.sdo'),
      ...standardColumnSizes.sdo,
      type: 'singleSelect',
      valueOptions: getSingleSelectOptions(standards, 'sdo'),
    },
    {
      ...defaultColumnProps,
      field: 'collectionName',
      headerName: t('labels.collectionName'),
      ...standardColumnSizes.collectionName,
      renderCell: ({
        value,
        row,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) =>
        row.lineType === 'StandardInCollection' && !value
          ? t('labels.collection')
          : value,
    },
    {
      ...defaultColumnProps,
      field: 'dateAdded',
      headerName: t('labels.addedDate'),
      type: 'date',
      valueGetter: (value: string | null) => value && new Date(value),
      renderCell: ({
        value,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) =>
        formatDate(value),
      ...standardColumnSizes.dateAdded,
    },
    {
      ...defaultColumnProps,
      field: 'lineSource',
      headerName: t('labels.lineSource'),
      ...standardColumnSizes.lineSource,
      type: 'singleSelect',
      valueOptions: getSingleSelectOptions(standards, 'lineSource'),
    },
    {
      ...defaultColumnProps,
      field: 'lineStatus',
      type: 'singleSelect',
      valueOptions: getStatusValueOptions(standards),
      valueGetter: (value: LineStatus) => t(`subscriptionStatuses.${value}`),
      renderCell: ({
        value,
        row,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) => (
        <StatusIndicator
          color={getStatusColor(row.lineStatus)}
          type="subscriptionLine"
        >
          {getStatusLabel(value, row)}
        </StatusIndicator>
      ),
      headerName: t('labels.status'),
      ...standardColumnSizes.lineStatus,
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: t('labels.actions'),
      ...standardColumnSizes.actions,
      renderCell: getRowActionButton,
      filterable: false,
      sortable: false,
    },
  ];

  const collectionColumns: GridColDef[] = [
    {
      ...defaultColumnProps,
      field: 'name',
      headerName: t('labels.collectionName'),
      ...collectionColumnSizes.name,
    },
    {
      ...defaultColumnProps,
      field: 'lineStatus',
      headerName: t('labels.status'),
      ...collectionColumnSizes.lineStatus,
      type: 'singleSelect',
      valueOptions: getStatusValueOptions(collections),
      valueGetter: (value: LineStatus) => t(`subscriptionStatuses.${value}`),
      renderCell: ({
        value,
        row,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) => (
        <StatusIndicator
          color={getStatusColor(row.lineStatus)}
          type="subscriptionLine"
        >
          {getStatusLabel(value, row)}
        </StatusIndicator>
      ),
    },
    {
      ...defaultColumnProps,
      field: 'dateAdded',
      headerName: t('labels.addedDate'),
      type: 'date',
      valueGetter: (value: string | null) => value && new Date(value),
      renderCell: ({
        value,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) =>
        formatDate(value),
      ...collectionColumnSizes.dateAdded,
    },
    {
      ...defaultColumnProps,
      field: 'lineSource',
      headerName: t('labels.lineSource'),
      ...collectionColumnSizes.lineSource,
      type: 'singleSelect',
      valueOptions: getSingleSelectOptions(collections, 'lineSource'),
    },
    {
      ...defaultColumnProps,
      field: 'collectionStandardCount',
      type: 'number',
      headerName: t('labels.productCount'),
      ...collectionColumnSizes.collectionStandardCount,
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: t('labels.actions'),
      ...collectionColumnSizes.actions,
      renderCell: getRowActionButton,
      filterable: false,
      sortable: false,
    },
  ];

  const eformColumns: GridColDef[] = [
    {
      ...defaultColumnProps,
      field: 'name',
      headerName: t('labels.eform'),
      ...eformColumnSizes.name,
    },
    {
      ...defaultColumnProps,
      field: 'sdo',
      headerName: t('labels.sdo'),
      ...eformColumnSizes.sdo,
      type: 'singleSelect',
      valueOptions: getSingleSelectOptions(eforms, 'sdo'),
    },
    {
      ...defaultColumnProps,
      field: 'dateAdded',
      headerName: t('labels.addedDate'),
      type: 'date',
      valueGetter: (value: string | null) => value && new Date(value),
      renderCell: ({
        value,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) =>
        formatDate(value),
      ...eformColumnSizes.dateAdded,
    },
    {
      ...defaultColumnProps,
      field: 'lineSource',
      headerName: t('labels.lineSource'),
      ...eformColumnSizes.lineSource,
      type: 'singleSelect',
      valueOptions: getSingleSelectOptions(eforms, 'lineSource'),
    },
    {
      ...defaultColumnProps,
      field: 'count',
      headerName: t('labels.count'),
      ...eformColumnSizes.count,
      type: 'number',
    },
    {
      ...defaultColumnProps,
      field: 'lineStatus',
      type: 'singleSelect',
      valueOptions: getStatusValueOptions(eforms),
      valueGetter: (value: LineStatus) => t(`subscriptionStatuses.${value}`),
      renderCell: ({
        value,
        row,
      }: GridCellParams<StandardModel | CollectionModel | EformModel, string>) => (
        <StatusIndicator
          color={getStatusColor(row.lineStatus)}
          type="subscriptionLine"
        >
          {getStatusLabel(value, row)}
        </StatusIndicator>
      ),
      headerName: t('labels.status'),
      ...eformColumnSizes.lineStatus,
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: t('labels.actions'),
      ...eformColumnSizes.actions,
      renderCell: getRowActionButton,
      filterable: false,
      sortable: false,
    },
  ];

  if (
    isFetchSubscriptionDetailsError &&
    isFetchStandardsError &&
    isFetchCollectionsError &&
    isFetchEformsError
  ) {
    return <NotFoundPage />;
  }

  if (
    isLoadingSubscriptionDetails ||
    isLoadingCollections ||
    isLoadingStandards ||
    isLoadingEforms
  ) {
    return <Spinner size="large" />;
  }

  return (
    <section className={styles.pageWrapper}>
      <ScreenReaderOnly>
        <h1>{t('subscriptions.subscriptionLines')}</h1>
      </ScreenReaderOnly>
      {activeSubscriptionLine ? (
        <DiscontinueSubscriptionLineModal
          isOpen={discontinueModalOpen}
          setIsOpen={setDiscontinueModalOpen}
          subscriptionLines={[activeSubscriptionLine]}
          refetch={refetch}
          datePickerMaxDate={subscriptionDetails?.invoicePeriodEndDate}
        />
      ) : null}
      <SubscriptionDetails
        subscriptionDetails={subscriptionDetails}
        className={styles.subscriptionDetails}
      />
      <Tabs
        size="md"
        className={styles.tabs}
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value as SubscriptionLinesTabEnum)}
      >
        <div className={styles.tabNavigation}>
          <LinkButton
            component={RouterLink}
            to={localizePathname(
              routes.subscribersSearch.to({
                q: subscriptionDetails?.customerId,
              }),
            )}
            icon="CornerUpLeft"
            variant="link"
          >
            <span>{t('subscriptions.returnToSubscriptions')}</span>
          </LinkButton>
          <TabList variant="outlined" className={styles.tabList}>
            <Tab
              disabled={
                !selectedTab ||
                (!standards?.length && !collections?.length) ||
                isFetchStandardsError
              }
              value="standards"
              disableIndicator
            >
              {t('labels.standards')}
            </Tab>
            <Tab
              disabled={!selectedTab || !collections?.length}
              value="collections"
              disableIndicator
            >
              {t('labels.collections')}
            </Tab>
            <Tab
              disabled={!selectedTab || !eforms?.length}
              value="eforms"
              disableIndicator
            >
              {t('labels.eforms')}
            </Tab>
          </TabList>
          {subscriptionId ? (
            <ProductSearch
              isEform={isEform}
              subscriptionId={subscriptionId}
              isActiveSubscription={
                subscriptionDetails?.subscriptionStatus === 'Active'
              }
            />
          ) : null}
        </div>
        <TabPanel value="standards" className={styles.tabPanel}>
          <SubscriptionLinesDataGrid
            key="standards"
            name="standards"
            rows={standards}
            columns={standardColumns}
            enableSearch
            setIncludeStandardsInCollections={
              collections && collections.length > 0
                ? setIncludeStandardsInCollections
                : undefined
            }
            includeStandardsInCollections={
              collections && collections.length > 0
                ? includeStandardsInCollections
                : undefined
            }
            refetch={refetch}
            filterModel={standardsFilterModel}
            setFilterModel={setStandardsFilterModel}
            datePickerMaxDate={subscriptionDetails?.invoicePeriodEndDate}
            subscriptionId={subscriptionDetails?.subscriptionId || ''}
            subscriptionStatus={subscriptionDetails?.subscriptionStatus}
            isFetching={isFetchingStandards}
          />
        </TabPanel>
        {collections && collections.length > 0 ? (
          <TabPanel value="collections" className={styles.tabPanel}>
            <SubscriptionLinesDataGrid
              key="collections"
              name="collections"
              rows={collections}
              columns={collectionColumns}
              refetch={refetch}
              filterModel={collectionsFilterModel}
              setFilterModel={setCollectionsFilterModel}
              datePickerMaxDate={subscriptionDetails?.invoicePeriodEndDate}
              subscriptionId={subscriptionDetails?.subscriptionId || ''}
              subscriptionStatus={subscriptionDetails?.subscriptionStatus}
              isFetching={isFetchingCollections}
            />
          </TabPanel>
        ) : null}
        {eforms && eforms.length > 0 ? (
          <TabPanel value="eforms" className={styles.tabPanel}>
            <SubscriptionLinesDataGrid
              key="eforms"
              name="eforms"
              rows={eforms}
              columns={eformColumns}
              refetch={refetch}
              filterModel={eformsFilterModel}
              setFilterModel={setEformsFilterModel}
              datePickerMaxDate={subscriptionDetails?.invoicePeriodEndDate}
              subscriptionId={subscriptionDetails?.subscriptionId || ''}
              subscriptionStatus={subscriptionDetails?.subscriptionStatus}
              isFetching={isFetchingEforms}
            />
          </TabPanel>
        ) : null}
      </Tabs>
    </section>
  );
}

export default SubscriptionLines;
